import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { LoginRequest, RegisterRequest, AuthResponse, ApiError } from "@/types/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { getTenantRootDomain, getTenantSubdomain } from "@/lib/tenant";
import { authService } from "@/services/authService";
import { broadcastAuthChanged, broadcastAuthLoggedOut } from "@/lib/authSync";
import { getPostAuthRoute } from "@/lib/routes";
import { localizeApiErrorDetail } from "@/lib/apiError";

const triggerDevLogoutOnHost = (targetHost: string) => {
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : "";
  const img = new Image();
  img.src = `${protocol}//${targetHost}${port}/api/v1/auth/logout`;
};

export const useAuth = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setAuth = useAuthStore((state) => state.setAuth);
  const logoutStore = useAuthStore((state) => state.logout);
  const navigate = useNavigate();

  const login = async (data: LoginRequest) => {
    setIsLoading(true);
    try {
      const response = await api.post<AuthResponse>("/api/v1/auth/login", data);
      setAuth(response.data.user, response.data.access_token);
      broadcastAuthChanged();
      toast.success(t("auth.welcomeBackToast"));
      navigate(getPostAuthRoute(window.location.hostname));
    } catch (error: any) {
      const apiError = error.response?.data as ApiError;
      const message =
        localizeApiErrorDetail(apiError?.detail, t, t("auth.invalidCredentials")) ??
        t("auth.invalidCredentials");
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterRequest) => {
    setIsLoading(true);
    try {
      const response = await api.post<AuthResponse>("/api/v1/auth/register", data);
      setAuth(response.data.user, response.data.access_token);
      broadcastAuthChanged();
      toast.success(t("auth.accountCreated"));
      navigate(getPostAuthRoute(window.location.hostname));
    } catch (error: any) {
      const apiError = error.response?.data as ApiError;
      let message = t("auth.registrationFailed");
      if (apiError?.detail) {
        const localized = localizeApiErrorDetail(apiError.detail, t, null);
        if (localized) {
          message = localized;
        } else if (typeof apiError.detail === "string") {
          message = apiError.detail;
        } else if (Array.isArray(apiError.detail)) {
          message = apiError.detail.map((err) => err.msg).join(", ");
        }
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    const currentUser = user;
    const hostname = window.location.hostname;
    const rootDomain = getTenantRootDomain(hostname);
    const isLocalhostRoot = rootDomain === "localhost";
    const currentSubdomain = getTenantSubdomain(hostname);
    let otherHost: string | null = null;

    if (isLocalhostRoot) {
      if (currentSubdomain) {
        otherHost = rootDomain;
      } else if (currentUser?.company_subdomain) {
        otherHost = `${currentUser.company_subdomain}.${rootDomain}`;
      }
    }

    try {
      await authService.logout();
    } catch {
      // Ignore logout errors; clear client state regardless.
    }
    if (otherHost && otherHost !== hostname) {
      triggerDevLogoutOnHost(otherHost);
    }
    logoutStore();
    broadcastAuthLoggedOut();
    navigate("/login");
    toast.info(t("auth.loggedOut"));
  };

  return {
    user,
    isAuthenticated,
    login,
    register,
    logout,
    isLoading,
  };
};
